import { useCallback, useEffect, useRef, useState } from "react";
import {
  deleteSavedFilter,
  getSavedFilter,
  hasUrlFilterParams,
  saveFilter,
} from "./savedFilter";

/**
 * Hook for loading / saving / deleting user-specific saved filters.
 *
 * @param {string} pageKey
 * @param {object} options
 * @param {boolean} options.enabled - when false, skip auto-apply (e.g. drill-down screens)
 * @param {boolean} options.ready - gate for when auto-apply may start (default true)
 * @param {function} options.onApply - called with { filter, values } when a saved filter should be applied
 * @param {function} options.onNoSavedFilter - called when there is no saved filter to apply
 * @param {function} options.onSkipUrlFilter - called when URL already has filter params (saved filter not applied)
 * @param {boolean} options.skipIfUrlFilter - skip auto-apply when URL has filter/bankId (default true)
 */
const useSavedFilter = (pageKey, options = {}) => {
  const {
    enabled = true,
    ready = true,
    onApply,
    onNoSavedFilter,
    onSkipUrlFilter,
    skipIfUrlFilter = true,
  } = options;

  const [hasSavedFilter, setHasSavedFilter] = useState(false);
  const [loading, setLoading] = useState(false);
  const appliedRef = useRef(false);
  const onApplyRef = useRef(onApply);
  const onNoSavedFilterRef = useRef(onNoSavedFilter);
  const onSkipUrlFilterRef = useRef(onSkipUrlFilter);
  onApplyRef.current = onApply;
  onNoSavedFilterRef.current = onNoSavedFilter;
  onSkipUrlFilterRef.current = onSkipUrlFilter;

  useEffect(() => {
    if (!enabled || !pageKey || !ready || appliedRef.current) return;

    appliedRef.current = true;

    if (skipIfUrlFilter && hasUrlFilterParams()) {
      getSavedFilter(pageKey).then((saved) => {
        setHasSavedFilter(Boolean(saved));
        if (onSkipUrlFilterRef.current) {
          onSkipUrlFilterRef.current();
        }
      });
      return;
    }

    setLoading(true);
    getSavedFilter(pageKey)
      .then((saved) => {
        if (saved && (saved.filter || saved.values)) {
          setHasSavedFilter(true);
          if (onApplyRef.current) {
            onApplyRef.current(saved);
          }
        } else {
          setHasSavedFilter(false);
          if (onNoSavedFilterRef.current) {
            onNoSavedFilterRef.current();
          }
        }
      })
      .finally(() => setLoading(false));
  }, [enabled, pageKey, ready, skipIfUrlFilter]);

  const saveCurrent = useCallback(
    (payload) => {
      if (!pageKey) return Promise.resolve();
      setLoading(true);
      return saveFilter(pageKey, payload)
        .then((response) => {
          if (response && response.success !== false) {
            setHasSavedFilter(true);
          }
          return response;
        })
        .finally(() => setLoading(false));
    },
    [pageKey]
  );

  const deleteCurrent = useCallback(() => {
    if (!pageKey) return Promise.resolve();
    setLoading(true);
    return deleteSavedFilter(pageKey)
      .then((response) => {
        if (response && response.success !== false) {
          setHasSavedFilter(false);
        }
        return response;
      })
      .finally(() => setLoading(false));
  }, [pageKey]);

  return {
    hasSavedFilter,
    setHasSavedFilter,
    loading,
    saveCurrent,
    deleteCurrent,
  };
};

export default useSavedFilter;
